import type { MenuKey } from '@/lib/menu-registry';

/**
 * Widget Registry
 *
 * Central registry of all available dashboard widgets.
 * Each widget has a unique key, display metadata, and a role-based
 * default config. SuperAdmin can override via WidgetAdminPage.
 *
 * Phase 1: Hardcoded config (this file)
 * Phase 2: Supabase table for per-role widget config
 */

export interface WidgetDefinition {
  key: string;
  name: string;
  description: string;
  category: 'personal' | 'team' | 'portfolio' | 'admin';
  defaultSize: 'full' | 'half' | 'third';
  /** Tilgangsnøkkel i Systemroller-matrisen som må være på for rollen. */
  menuKey?: MenuKey;
}

export interface RoleWidgetConfig {
  role: string;
  /** `section` gir en overskrift over widgeten i gridet (ny Måltall). */
  widgets: { key: string; order: number; size: 'full' | 'half' | 'third'; section?: string }[];
}

// All available widgets in the system
export const WIDGET_DEFINITIONS: WidgetDefinition[] = [
  {
    key: 'budsjettavvik-kompakt',
    name: 'Budsjettavvik (kompakt)',
    description: 'De fem største budsjettavvikene per ansatt begge veier, med hele lista og oppgavene i sidepanelet',
    category: 'admin',
    defaultSize: 'half',
  },
  {
    key: 'bf-maal-ansatt',
    name: 'GEEK-mål per selger (ansatt)',
    description: 'GEEK-mål per selger for ansatte som ikke selv er med i en salgsenhet',
    category: 'team',
    defaultSize: 'half',
  },
  {
    key: 'maaltall-sjekker',
    name: 'Dette bør du se på',
    description: 'Faste sjekker som finner det som trenger handling, med listen bak hvert funn',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'faktureringsgrad-team',
    name: 'Faktureringsgrad med kapasitetsfordeling',
    description: 'Teamets grad, hvor kapasiteten gikk og søyler per måned, med drill ned til ansatt og dag',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'feilbudsjettering-produkt',
    name: 'Feilbudsjettering per produkt',
    description: 'Produkter vi selger for mange eller for få timer av, med linjene og oppgavene bak',
    category: 'team',
    defaultSize: 'half',
  },
  {
    key: 'prosjekter-som-venter',
    name: 'Prosjekter som venter',
    description: 'Prosjekter klare til å avsluttes og rest som skulle vært fakturert',
    category: 'admin',
    defaultSize: 'half',
  },
  {
    key: 'topp5-kompakt',
    name: 'Topp 5 prosjekter',
    description: 'Beste timepris eller GEEK i ett kort',
    category: 'team',
    defaultSize: 'half',
  },
  {
    key: 'reell-timepris',
    name: 'Reell timepris',
    description: 'Reell timepris per selger eller prosjektleder i ett kort',
    category: 'admin',
    defaultSize: 'half',
  },
  {
    key: 'mine-tall',
    name: 'Mine tall',
    description: 'Egen faktureringsgrad og budsjettavvik, med dager uten føring',
    category: 'personal',
    defaultSize: 'full',
  },
  {
    key: 'mitt-salg',
    name: 'Mitt salg',
    description: 'Salgsenhetens GEEK mot mål og timepris som selger',
    category: 'personal',
    defaultSize: 'half',
  },
  {
    key: 'maaltall-kpi',
    name: 'Nøkkeltall',
    description: 'Omsetning hittil i år, resultat, GEEK solgt denne måneden og GEEK i tilbudspipen, med grunnlag per flis',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'bonusmal-kompakt',
    name: 'Bonusmål (kompakt)',
    description: 'De tre bonusmålene på én rad, med grunnlaget bak hvert mål',
    category: 'team',
    defaultSize: 'full',
  },
  {
    key: 'bonusmal',
    name: 'Bonusmål 2026',
    description: 'Tre felles bonusmål med fremgangsvisning (GEEK, faktureringsgrad, budsjettavvik)',
    category: 'team',
    defaultSize: 'full',
  },
  {
    key: 'faktureringsgrad-ansatt',
    name: 'Faktureringsgrad — ansatt',
    description: 'Personlig måneds- og kvartalsoversikt med snitt hittil i år, mål-linje og månedslukking',
    category: 'personal',
    defaultSize: 'full',
  },
  {
    key: 'topp5-prosjekter',
    name: 'Topp 5 prosjekter',
    description: 'To rangeringer: beste reelle timepris (>100 solgte timer) og høyest GEEK blant prosjekter som kom inn under budsjett, med kreditering av salg og PL',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'faktureringsgrad-ledelse',
    name: 'Faktureringsgrad — ledelse',
    description: 'Teamets samlede faktureringsgrad per måned mot 65 %-mål, med drill-down per måned og ansatt',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'bf-maal',
    name: 'GEEK-mål',
    description: 'GEEK-mål per ansvarlig med fremgang',
    category: 'team',
    defaultSize: 'half',
  },
  {
    key: 'feilbudsjettering-ledelse',
    name: 'Feilbudsjettering — ledelse',
    description: 'Budsjettavvik per ansatt: standard godkjente oppgaver (fullført-dato) eller ferdigstilte prosjekter, målt mot teamsnittet',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'min-feilbudsjettering',
    name: 'Mine oppgaver — tall og effektivitet',
    description: 'Egne solgte og brukte timer på ferdigstilte prosjekter, med oppgave-drilldown og sammenligning mot teamsnittet',
    category: 'personal',
    defaultSize: 'full',
  },
  {
    key: 'prognose-fakturering',
    name: 'Prognose fakturering',
    description: 'Forventet fakturainntekt per måned: fakturert (grønt) og estimert rest (rødt) basert på kontraktssum og estimert deadline',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'ytd-hovedtall',
    name: 'Hovedtall hittil i år',
    description: 'Omsetning, bruttofortjeneste og resultat (P&L) fra PowerOffice hittil i år, hver med delta mot samme periode i fjor',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'bf-over-tid',
    name: 'GEEK over tid',
    description: 'GEEK per måned/kvartal: reell (avsluttet) vs estimert + prognose framover. Klikk en periode for prosjektene bak (treffsikkerhet).',
    category: 'admin',
    defaultSize: 'full',
  },
  {
    key: 'sales-unit-hourly-rate',
    name: 'Reell timepris per selger',
    description: 'Reell GEEK delt på førte timer for fullførte prosjekter per selger, med drilldown',
    category: 'admin',
    defaultSize: 'full',
    menuKey: 'timepris-selger',
  },
  {
    key: 'project-lead-hourly-rate',
    name: 'Reell timepris per prosjektleder',
    description: 'Reell GEEK delt på førte timer for fullførte prosjekter per prosjektleder, med drilldown',
    category: 'admin',
    defaultSize: 'full',
    menuKey: 'timepris-prosjektleder',
  },
];

// Default widget layout per role.
// VIB-708-followup: rolle-keys harmonisert med users.role-skjemaet
// (user/admin/superadmin/external). Tidligere legacy-keys
// (employee/kunderadgiver) er borte; admin-rolla beholder den rike
// dashbordet, user/superadmin/external får sine egne default-sett.
const USER_DEFAULT_WIDGETS: RoleWidgetConfig['widgets'] = [
  { key: 'bonusmal', order: 1, size: 'full' },
  { key: 'faktureringsgrad-ansatt', order: 2, size: 'full' },
  { key: 'min-feilbudsjettering', order: 3, size: 'full' },
];

const ADMIN_DEFAULT_WIDGETS: RoleWidgetConfig['widgets'] = [
  { key: 'bonusmal', order: 1, size: 'full' },
  { key: 'faktureringsgrad-ansatt', order: 2, size: 'full' },
  { key: 'topp5-prosjekter', order: 3, size: 'full' },
  { key: 'bf-maal', order: 4, size: 'half' },
  { key: 'feilbudsjettering-ledelse', order: 5, size: 'full' },
  { key: 'faktureringsgrad-ledelse', order: 6, size: 'full' },
  { key: 'min-feilbudsjettering', order: 7, size: 'full' },
  { key: 'prognose-fakturering', order: 8, size: 'full' },
  { key: 'bf-over-tid', order: 10, size: 'full' },
  { key: 'sales-unit-hourly-rate', order: 11, size: 'full' },
  { key: 'project-lead-hourly-rate', order: 12, size: 'full' },
];

export const DEFAULT_ROLE_CONFIGS: RoleWidgetConfig[] = [
  { role: 'user', widgets: USER_DEFAULT_WIDGETS },
  { role: 'admin', widgets: ADMIN_DEFAULT_WIDGETS },
  // Superadmin ser samme som admin i utgangspunktet — admin kan splitte
  // disse senere via WidgetAdminEditor hvis det blir aktuelt.
  { role: 'superadmin', widgets: ADMIN_DEFAULT_WIDGETS },
  // Eksterne brukere: de 10 legacy-widgetene er slettet (VIB-1330) — config er tom
  // inntil nye external-widgets lages ved behov.
  {
    role: 'external',
    widgets: [],
  },
];

/**
 * Ny Måltall bak `feature_maaltall_v2` (VIB-2990), i rekkefølgen fra godkjent
 * wireframe. Leses fra kode, ikke `widget_configs`, så prod-raden kan ryddes
 * først når flagget står på for alle (VIB-2993). Listene fylles med nye
 * widgets etter hvert som de bygges.
 */
const V2_USER_WIDGETS: RoleWidgetConfig['widgets'] = [
  { key: 'bonusmal-kompakt', order: 1, size: 'full' },
  { key: 'mine-tall', order: 2, size: 'full' },
  // Mitt salg vises for medlemmer av en salgsenhet og erstatter GEEK-mål per
  // selger for dem. Widgets som ikke gjelder brukeren rendrer ingenting.
  { key: 'mitt-salg', order: 3, size: 'half' },
  { key: 'topp5-kompakt', order: 4, size: 'half' },
  { key: 'bf-maal-ansatt', order: 5, size: 'half' },
  { key: 'feilbudsjettering-produkt', order: 6, size: 'full' },
];

const V2_ADMIN_WIDGETS: RoleWidgetConfig['widgets'] = [
  { key: 'maaltall-kpi', order: 1, size: 'full', section: 'Selskapet nå' },
  { key: 'bonusmal-kompakt', order: 2, size: 'full' },
  { key: 'maaltall-sjekker', order: 3, size: 'full' },
  { key: 'faktureringsgrad-team', order: 4, size: 'full', section: 'Team' },
  { key: 'budsjettavvik-kompakt', order: 5, size: 'half' },
  { key: 'feilbudsjettering-produkt', order: 6, size: 'half' },
  { key: 'prognose-fakturering', order: 7, size: 'full', section: 'Økonomi framover' },
  { key: 'prosjekter-som-venter', order: 8, size: 'half' },
  { key: 'bf-maal', order: 9, size: 'half' },
  { key: 'topp5-kompakt', order: 10, size: 'half', section: 'Rangering' },
  { key: 'reell-timepris', order: 11, size: 'half' },
];

export function getV2WidgetsForRole(role: string): RoleWidgetConfig['widgets'] {
  if (role === 'admin' || role === 'superadmin') return V2_ADMIN_WIDGETS;
  if (role === 'external') return [];
  return V2_USER_WIDGETS;
}

/** Get widget config for a role, falling back to user-default. */
export function getWidgetsForRole(role: string): RoleWidgetConfig['widgets'] {
  const config = DEFAULT_ROLE_CONFIGS.find((c) => c.role === role);
  return config?.widgets ?? USER_DEFAULT_WIDGETS;
}

/** Get widget definition by key */
export function getWidgetDefinition(key: string): WidgetDefinition | undefined {
  return WIDGET_DEFINITIONS.find((w) => w.key === key);
}

/** Fjerner tilgangsstyrte widgets før gridet rendres, så manglende tilgang ikke gir tomme hull. */
export function filterWidgetsByMenuAccess(
  widgets: RoleWidgetConfig['widgets'],
  access: Partial<Record<MenuKey, boolean>>,
): RoleWidgetConfig['widgets'] {
  return widgets.filter((widget) => {
    const menuKey = getWidgetDefinition(widget.key)?.menuKey;
    return menuKey == null || access[menuKey] === true;
  });
}

/** Minimal shape we accept for overrides — keeps this module free of hook imports */
export interface WidgetOverrideInput {
  widget_id: string;
  visible: boolean;
  display_order: number | null;
}

/**
 * Merge role default widgets with per-user overrides.
 *
 * - `visible=false` in an override hides the widget
 * - `display_order` in an override replaces the role-default order
 * - Widgets without overrides keep their role-default config unchanged
 *
 * Overrides for widget keys that are not part of the role default are
 * ignored — a hidden override on a widget the user never had access to
 * is a no-op, and we do not invent widgets to add. Surface visibility is
 * still gated by the role default set.
 */
export function mergeWidgetOverrides(
  roleWidgets: RoleWidgetConfig['widgets'],
  overrides: WidgetOverrideInput[],
): RoleWidgetConfig['widgets'] {
  const overrideMap = new Map<string, WidgetOverrideInput>();
  for (const o of overrides) overrideMap.set(o.widget_id, o);

  return roleWidgets
    .filter((w) => {
      const o = overrideMap.get(w.key);
      return o ? o.visible : true;
    })
    .map((w) => {
      const o = overrideMap.get(w.key);
      return o?.display_order != null
        ? { ...w, order: o.display_order }
        : w;
    });
}
